import React from 'react'
import { Button, Paper, Stack, Typography } from '@mui/material'
import { SEVERITY } from '../makeReady/severity'
import { BOARD_GROUPS } from './BoardLocation'

// Where the whole fleet needs attention, from the server's counts (the grid
// only holds one group/page): one row per group and level, e.g. "12 bags
// with expired stock". Clicking a row switches the board to that group and
// level.
const ATTENTION_LEVELS = [
  { level: 'expired', text: 'with expired stock' },
  { level: 'missing', text: 'missing stock' },
  { level: 'expiringSoon', text: 'with stock expiring soon' },
  { level: 'neverChecked', text: 'never checked' },
]

const noun = (group, n) =>
  n === 1 ? group.singular : group.key === 'assets' ? 'other assets' : group.label.toLowerCase()

const AttentionFeed = ({ counts, onSelect }) => {
  const rows = ATTENTION_LEVELS.flatMap(({ level, text }) =>
    BOARD_GROUPS.filter((group) => counts[group.key]?.[level] > 0).map(
      (group) => {
        const n = counts[group.key][level]
        return { key: `${group.key}-${level}`, group, level, label: `${n} ${noun(group, n)} ${text}` }
      },
    ),
  )
  const untracked = BOARD_GROUPS.filter(
    (group) => counts[group.key]?.untracked > 0,
  )

  return (
    <Paper sx={{ p: 1.5 }}>
      <Typography variant="title2" fontSize="1rem" sx={{ mb: 1 }}>
        Needs Attention
      </Typography>
      <Stack gap={1}>
        {untracked.map((group) => {
          const n = counts[group.key].untracked
          return (
            <Stack
              key={`${group.key}-untracked`}
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              gap={1}
              sx={{
                p: 1,
                borderLeft: `4px dashed ${SEVERITY.untracked.hex}`,
                bgcolor: '#F8FAFC',
                borderRadius: 1,
              }}
            >
              <Typography variant="body2" fontWeight={600} color="#64748B">
                {n} {noun(group, n)} {n === 1 ? 'is' : 'are'} not tracked
              </Typography>
              <Button
                size="small"
                variant="outlined"
                sx={{ whiteSpace: 'nowrap' }}
                onClick={() => onSelect(group.key, 'untracked')}
              >
                Assign
              </Button>
            </Stack>
          )
        })}
        {rows.length === 0 && (
          <Typography variant="body2" color="success.main">
            Nothing needs attention across the tracked fleet right now.
          </Typography>
        )}
        {rows.map((row) => (
          <Stack
            key={row.key}
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            gap={1}
            onClick={() => onSelect(row.group.key, row.level)}
            sx={{
              p: 1,
              borderLeft: '4px solid',
              borderLeftColor: SEVERITY[row.level].hex,
              borderRadius: 1,
              cursor: 'pointer',
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <Typography variant="body2" fontWeight={600}>
              {row.label}
            </Typography>
            <Typography
              variant="caption"
              fontWeight={700}
              color={SEVERITY[row.level].hex}
              whiteSpace="nowrap"
            >
              View
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Paper>
  )
}

export default AttentionFeed
